<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import type { ISiteNotice } from '~/interfaces/stores/site.store.interface';
import type { IRestoreData } from '~/interfaces/dto/general/toast-messages';
import { RestoreHistoryIcon } from '~/components/design-system/icons/index';

const site = useSiteStore();

const props = withDefaults(defineProps<ISiteNotice>(), {
  severity: 'success',
  heading: '',
  position: 'br',
  timeout: 3000,
  enabled: false,
  content: () => ({
    type: 'message',
    data: '',
  }),
});

const positionClasses = computed(() => {
  const base = 'fixed z-50 transition-all duration-300';
  const bottom = 'bottom-[70px] sm:bottom-2';

  const positionMap: Record<string, string> = {
    tl: 'top-4 left-4',
    tr: 'top-4 right-4',
    bl: `${bottom} left-4`,
    tc: 'top-4 left-1/2 -translate-x-1/2',
    bc: `${bottom} left-1/2 -translate-x-1/2`,
    c: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    br: `${bottom} right-2`,
  };

  return `${base} ${positionMap[props.position] ?? positionMap.br}`;
});

const returnIcon = computed<unknown>(() => {
  switch (props.severity) {
    case 'success':
      return CheckCircleIcon;
    case 'error':
      return XCircleIcon;
    case 'warn':
    case 'warning':
      return ExclamationCircleIcon;
    case 'restore':
      return RestoreHistoryIcon;
    default:
      return CheckCircleIcon;
  }
});

const severityColour = computed<(icon: boolean) => string>(
  () =>
    (icon: boolean = true): string => {
      switch (props.severity) {
        case 'error':
          return icon ? 'text-error-500' : 'bg-error-500';
        case 'warning':
        case 'warn':
          return icon ? 'text-gold-500' : 'bg-gold-500';
        case 'restore':
        case 'success':
        default:
          return icon ? 'text-primary-blue-600' : 'bg-primary-blue-600';
      }
    },
);

const progressBar = ref<number>(100);
const animationFrame = ref<number | null>(null);
const closeTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const startTimers = () => {
  clearTimers(false);
  progressBar.value = 100;

  const startTime = performance.now();
  const duration = props.timeout;

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const remaining = Math.max(0, 100 - (elapsed / duration) * 100);

    progressBar.value = remaining;

    if (remaining > 0) animationFrame.value = requestAnimationFrame(animate);
  };

  animationFrame.value = requestAnimationFrame(animate);
  closeTimer.value = setTimeout(closeToaster, props.timeout);
};

const clearTimers = (shouldClose = true) => {
  if (closeTimer.value) clearTimeout(closeTimer.value);
  closeTimer.value = null;

  if (shouldClose) {
    site.toggleSiteNotice({
      heading: '',
      content: {
        type: props.content?.type || 'message',
        data: '',
      },
      severity: 'error',
      enabled: false,
      timeout: 3000,
    });
    progressBar.value = 0;
  }
};

const closeToaster = () => clearTimers(true);

watch(
  () => props.enabled,
  (isEnabled) => {
    if (isEnabled) startTimers();
    else clearTimers(false);
  },
  { immediate: true },
);

watch(
  () => [props.heading, props.content],
  () => {
    if (props.enabled) startTimers();
  },
);
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="enabled"
        :class="[
          positionClasses,
          'bg-stacked rounded-md shadow-lg',
          {
            'w-72 sm:w-80': content?.type === 'message',
            'w-44 sm:w-56': content?.type !== 'message',
          },
        ]"
        role="alert"
      >
        <div>
          <div
            class="flex place-content-between text-base-priority rounded-t-lg"
          >
            <div
              :class="[
                'flex place-items-center p-2',
                { 'pr-0': content?.type === 'restore-session' },
              ]"
            >
              <div
                :class="[
                  severityColour(true),
                  'max-w-8 max-h-8 font-bold flex-center rounded-md',
                  {
                    'mr-2': content?.type === 'message',
                    'mr-1 sm:mr-2': content?.type !== 'message',
                  },
                ]"
              >
                <component
                  :is="returnIcon"
                  :key="severity"
                  :class="['font-extrabold w-6 h-6', severityColour(true)]"
                />
              </div>
              <div
                class="font-bold"
                :class="{
                  'text-sm sm:text-base-size': content?.type === 'message',
                  'text-xs sm:text-sm': content?.type !== 'message',
                }"
              >
                {{ heading }}
              </div>
            </div>

            <DesignSystemButton
              type="tertiary"
              size="small"
              padding="sm"
              rounding="md"
              :class="{
                'm-2': content?.type === 'message',
                'ml-0 my-2 mr-1.5 sm:mx-2': content?.type !== 'message',
              }"
              @click="closeToaster"
            >
              <XMarkIcon />
            </DesignSystemButton>
          </div>

          <div
            v-if="content?.type === 'message'"
            class="p-4 text-center text-base-priority bg-primary"
          >
            {{ content.data }}
          </div>
          <div v-else-if="content?.type === 'restore-session'">
            <LazyUserRestoreSession
              :restore-data="content?.data as IRestoreData"
              @close-toast="closeToaster"
            />
          </div>
          <div
            class="w-full bg-light-200 dark:bg-dark-800 h-1.5 rounded-b-lg overflow-hidden"
          >
            <div
              class="h-full transition-all duration-200 ease-linear"
              :class="[severityColour(false)]"
              :style="{ width: `${progressBar}%` }"
            ></div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
